import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { CheckCircle, ArrowRight, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

function ConflictNavigator({ onBack }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [gameState, setGameState] = useState('intro'); // 'intro', 'playing', 'completed'

  const conflictSteps = [
    {
      step: 1,
      title: "Notice your jackal show",
      description: "Enjoy it if you want to! What jackal thoughts are you having?",
      prompt: "What critical, blaming, or judging thoughts do you have about the other person?",
      example: "They are such a control freak. They never listen to me."
    },
    {
      step: 2, 
      title: "Give yourself empathy",
      description: "For any jackal things the other person said or did",
      prompt: "What did they say/do that hurt? How do you feel about it?",
      example: "When they said 'You're wrong again', I felt angry and hurt because I need respect."
    },
    {
      step: 3,
      title: "Translate your jackal thoughts",
      description: "Turn judgments of them into your feelings and needs",
      prompt: "What feelings and needs are underneath your judgments?",
      example: "'They're controlling' → I feel frustrated because I need autonomy and choice."
    },
    {
      step: 4,
      title: "Translate self-jackal thoughts", 
      description: "Turn judgments of yourself into feelings and needs",
      prompt: "What mean things do you think about yourself? What's underneath?",
      example: "'I'm stupid' → I feel sad because I need understanding and acceptance."
    },
    {
      step: 5,
      title: "Self-Empathy OFNR",
      description: "Go through full OFNR for each jackal translation",
      prompt: "For each judgment, create: When I... I feel... because I need... Would I be willing to...?",
      example: "When I think 'I'm stupid', I feel discouraged because I need self-compassion. Would I be willing to speak kindly to myself?"
    }
  ];

  const handleStepComplete = (stepIndex, response) => {
    setResponses(prev => ({ ...prev, [stepIndex]: response }));
    
    // Play completion sound
    const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAxNTU1NTU1NTU1NTU1NTU1NTU1');
    audio.volume = 0.4;
    audio.play().catch(() => {});
    
    if (stepIndex < conflictSteps.length - 1) {
      setCurrentStep(stepIndex + 1);
    } else {
      setGameState('completed');
      toast.success('🎉 Conflict Navigator complete! You\'re ready to engage with empathy.');
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_belonging-sessions/artifacts/femlgvnm_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="min-h-screen bg-black bg-opacity-70 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          <div className="text-center mb-8">
            <Button onClick={onBack} variant="outline" className="border-white text-white hover:bg-white hover:text-black mb-6" style={{ borderRadius: 0 }}>
              ← Back to Games & Tools
            </Button>
            <h1 className="text-4xl font-bold text-white mb-4">🔥 Conflict Navigator</h1>
            <p className="text-xl text-gray-300">Transform conflict into connection with this 30-step guided process</p>
          </div>

          {gameState === 'intro' && (
            <Card className="border-4 border-red-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-2xl text-red-800">Ready to Navigate Conflict?</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    This interactive process will guide you through 30 steps to transform any conflict into deeper connection.
                  </p>
                  <p className="text-base text-gray-600">
                    Think of a current conflict or difficult relationship. We'll work through it together step by step.
                  </p>
                  <div className="bg-red-50 p-4 border-l-4 border-red-500">
                    <p className="text-sm text-red-800 font-bold">Remember:</p>
                    <p className="text-sm text-red-700">The goal isn't to get your way, but to create connection where everyone's needs are met.</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setGameState('playing')}
                  className="w-full mt-6 bg-red-600 text-white hover:bg-red-700 py-4 text-lg"
                  style={{ borderRadius: 0 }}
                >
                  🚀 Begin Conflict Navigation
                </Button>
              </CardContent>
            </Card>
          )}

          {gameState === 'playing' && (
            <Card className="border-4 border-red-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-xl text-red-800">
                    Step {conflictSteps[currentStep].step}: {conflictSteps[currentStep].title}
                  </CardTitle>
                  <div className="text-sm text-gray-600">
                    {currentStep + 1} of {conflictSteps.length}
                  </div>
                </div>
                <Progress value={(currentStep / conflictSteps.length) * 100} className="mt-3" />
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <p className="text-lg text-gray-800">{conflictSteps[currentStep].description}</p>
                  
                  <div className="bg-blue-50 p-4 border-l-4 border-blue-500">
                    <p className="text-sm text-blue-800 font-bold mb-2">Example:</p>
                    <p className="text-sm text-blue-700 italic">{conflictSteps[currentStep].example}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold mb-2">{conflictSteps[currentStep].prompt}</label>
                    <Textarea
                      placeholder="Your response..."
                      className="border-red-500 min-h-[120px]"
                      style={{ borderRadius: 0 }}
                      value={responses[currentStep] || ''}
                      onChange={(e) => setResponses(prev => ({ ...prev, [currentStep]: e.target.value }))}
                    />
                  </div>
                  
                  <div className="flex gap-4">
                    {currentStep > 0 && (
                      <Button 
                        onClick={() => setCurrentStep(currentStep - 1)}
                        variant="outline"
                        className="border-red-500 text-red-600"
                        style={{ borderRadius: 0 }}
                      >
                        ← Previous Step
                      </Button>
                    )}
                    <Button 
                      onClick={() => handleStepComplete(currentStep, responses[currentStep])}
                      className="bg-red-600 text-white hover:bg-red-700 flex-1"
                      style={{ borderRadius: 0 }}
                      disabled={!responses[currentStep] || responses[currentStep].trim().length === 0}
                    >
                      Complete Step <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {gameState === 'completed' && (
            <Card className="border-4 border-green-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-2xl text-green-800 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  Conflict Navigation Complete!
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="bg-green-50 p-6 border-l-4 border-green-500">
                    <h3 className="font-bold text-green-800 mb-3">🎉 You're Ready for Connection!</h3>
                    <p className="text-green-700">
                      You've transformed your enemy images into feelings and needs. 
                      You're now prepared to engage the other person with empathy and authenticity.
                    </p>
                  </div>
                  
                  <div className="text-center space-y-4">
                    <Button 
                      onClick={() => {
                        setGameState('intro');
                        setCurrentStep(0);
                        setResponses({});
                      }}
                      className="bg-red-600 text-white hover:bg-red-700"
                      style={{ borderRadius: 0 }}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Navigate Another Conflict
                    </Button>
                    <Button 
                      onClick={onBack}
                      variant="outline"
                      className="border-black ml-4"
                      style={{ borderRadius: 0 }}
                    >
                      Back to Games & Tools
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}

export default ConflictNavigator;